import React, { useState } from 'react';
import { PocketGridRetroPalette } from '../theme/PocketGridRetroTheme';
import { SavedSequenceSlot } from './MainPerformanceView';

interface SaveSequenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSequenceTitle: string;
  activeSlotIndex: number;
  stepCountMode: 16 | 21;
  bpm: number;
  sequenceBank: SavedSequenceSlot[];
  onSaveToSlot: (slotIndex: number, customTitle: string) => void;
  onLoadSlot: (slot: SavedSequenceSlot) => void;
  onRenameSlot: (slotIndex: number, newTitle: string) => void;
  onClearSlot: (slotIndex: number) => void;
}

export const SaveSequenceModal: React.FC<SaveSequenceModalProps> = ({
  isOpen,
  onClose,
  currentSequenceTitle,
  activeSlotIndex,
  stepCountMode,
  bpm,
  sequenceBank,
  onSaveToSlot,
  onLoadSlot,
  onRenameSlot,
  onClearSlot,
}) => {
  const [activeTitleInput, setActiveTitleInput] = useState<string>(currentSequenceTitle);
  const [selectedSlotTarget, setSelectedSlotTarget] = useState<number>(activeSlotIndex || 1);
  const [editingSlotIdx, setEditingSlotIdx] = useState<number | null>(null);
  const [editingSlotName, setEditingSlotName] = useState<string>('');

  if (!isOpen) return null;

  const handleQuickSave = () => {
    const finalTitle = activeTitleInput.trim() || `Groove Slot ${selectedSlotTarget}`;
    onSaveToSlot(selectedSlotTarget, finalTitle);
    onClose();
  };

  const handleStartRename = (slotIndex: number, currentName: string) => {
    setEditingSlotIdx(slotIndex);
    setEditingSlotName(currentName.replace(/^\d+:\s*/, ''));
  };

  const handleConfirmRename = (slotIndex: number) => {
    if (editingSlotName.trim()) {
      onRenameSlot(slotIndex, editingSlotName.trim());
    }
    setEditingSlotIdx(null);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.82)',
        backdropFilter: 'blur(5px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px',
        boxSizing: 'border-box',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 'min(98vw, 720px)',
          height: '100%',
          maxHeight: 'min(92dvh, calc(100vh - 16px))',
          background: '#151619',
          border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
          borderRadius: '8px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.95)',
          display: 'flex',
          flexDirection: 'column',
          color: PocketGridRetroPalette.warmIvoryText,
          overflow: 'hidden',
          boxSizing: 'border-box',
          fontFamily: '"Inter", -apple-system, system-ui, sans-serif',
        }}
      >
        {/* Pinned Modal Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 14px',
            borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            background: '#1C1D21',
            flexShrink: 0,
            minHeight: '40px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '15px' }}>🗂</span>
            <div>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 800,
                  color: PocketGridRetroPalette.tungstenAmber,
                  letterSpacing: '0.04em',
                }}
              >
                SEQUENCE BANK MANAGER & SLOTS
              </div>
              <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText }}>
                Name, save, and recall up to 8 independent multi-track sequences with custom titles
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: '#2A2C31',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              color: PocketGridRetroPalette.warmIvoryText,
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 800,
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>

        {/* Current Active Sequence Quick-Save Bar */}
        <div
          style={{
            padding: '8px 14px',
            background: '#191B1F',
            borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '10px', fontWeight: 800, color: PocketGridRetroPalette.incandescentCream }}>
            Sequence Name:
          </span>
          <input
            type="text"
            value={activeTitleInput}
            onChange={(e) => setActiveTitleInput(e.target.value)}
            placeholder="e.g. Neo-Tokyo 84 Groove"
            style={{
              flex: 1,
              minWidth: '160px',
              height: '26px',
              padding: '0 8px',
              borderRadius: '4px',
              background: '#101114',
              border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
              color: PocketGridRetroPalette.incandescentCream,
              fontSize: '11px',
              fontWeight: 700,
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '9.5px', color: PocketGridRetroPalette.mutedPewterText }}>
              Destination:
            </span>
            <select
              value={selectedSlotTarget}
              onChange={(e) => setSelectedSlotTarget(Number(e.target.value))}
              style={{
                height: '26px',
                background: '#101114',
                color: PocketGridRetroPalette.tungstenAmber,
                border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 800,
                padding: '0 6px',
              }}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>
                  SLOT #{n}
                </option>
              ))}
            </select>

            <button
              onClick={handleQuickSave}
              style={{
                height: '26px',
                padding: '0 12px',
                borderRadius: '4px',
                background: PocketGridRetroPalette.phosphorMoss,
                border: `1px solid ${PocketGridRetroPalette.incandescentCream}`,
                color: PocketGridRetroPalette.incandescentCream,
                fontSize: '10px',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: '0 0 8px rgba(78, 178, 114, 0.35)',
              }}
            >
              💾 SAVE SEQUENCE
            </button>
          </div>
        </div>

        {/* Scrollable Slot Cards List */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '10px 14px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '8px',
            minHeight: 0,
            boxSizing: 'border-box',
          }}
        >
          {sequenceBank.map((slot) => {
            const isActive = activeSlotIndex === slot.slotIndex;
            const isEditing = editingSlotIdx === slot.slotIndex;

            return (
              <div
                key={slot.slotIndex}
                style={{
                  background: isActive ? '#242018' : '#18191D',
                  border: `1.5px solid ${
                    isActive
                      ? PocketGridRetroPalette.tungstenAmber
                      : slot.isPopulated
                      ? PocketGridRetroPalette.panelBorder
                      : '#24262B'
                  }`,
                  borderRadius: '6px',
                  padding: '8px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  boxShadow: isActive ? '0 0 10px rgba(217, 148, 59, 0.25)' : 'none',
                }}
              >
                {/* Slot Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span
                      style={{
                        padding: '1px 5px',
                        borderRadius: '3px',
                        background: isActive ? PocketGridRetroPalette.tungstenAmber : '#2A2C31',
                        color: isActive ? '#151619' : PocketGridRetroPalette.warmIvoryText,
                        fontSize: '9px',
                        fontWeight: 900,
                      }}
                    >
                      SLOT {slot.slotIndex}
                    </span>
                    {isActive && (
                      <span
                        style={{
                          fontSize: '8px',
                          fontWeight: 800,
                          color: PocketGridRetroPalette.phosphorMoss,
                          letterSpacing: '0.04em',
                        }}
                      >
                        ● LOADED NOW
                      </span>
                    )}
                  </div>

                  <span
                    style={{
                      fontSize: '8.5px',
                      color: slot.isPopulated ? PocketGridRetroPalette.vintageVfdSage : PocketGridRetroPalette.mutedPewterText,
                    }}
                  >
                    {slot.isPopulated ? `${slot.stepCount} Steps • ${slot.bpm} BPM` : 'EMPTY'}
                  </span>
                </div>

                {/* Slot Name / Rename Input */}
                {isEditing ? (
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={editingSlotName}
                      onChange={(e) => setEditingSlotName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleConfirmRename(slot.slotIndex);
                        if (e.key === 'Escape') setEditingSlotIdx(null);
                      }}
                      autoFocus
                      style={{
                        flex: 1,
                        height: '22px',
                        padding: '0 6px',
                        borderRadius: '3px',
                        background: '#101114',
                        border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                        color: PocketGridRetroPalette.incandescentCream,
                        fontSize: '10px',
                        fontWeight: 700,
                      }}
                    />
                    <button
                      onClick={() => handleConfirmRename(slot.slotIndex)}
                      style={{
                        height: '22px',
                        padding: '0 6px',
                        borderRadius: '3px',
                        background: PocketGridRetroPalette.phosphorMoss,
                        border: 'none',
                        color: '#FFF',
                        fontSize: '9px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => setEditingSlotIdx(null)}
                      style={{
                        height: '22px',
                        padding: '0 5px',
                        borderRadius: '3px',
                        background: '#2A2C31',
                        border: 'none',
                        color: PocketGridRetroPalette.mutedPewterText,
                        fontSize: '9px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 800,
                      color: PocketGridRetroPalette.incandescentCream,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {slot.name}
                  </div>
                )}

                {/* Slot Buttons */}
                <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                  <button
                    onClick={() => {
                      onLoadSlot(slot);
                      onClose();
                    }}
                    title="Load this sequence into active sequencer"
                    style={{
                      flex: 1,
                      height: '22px',
                      borderRadius: '3px',
                      background: '#1F2420',
                      border: `1px solid ${PocketGridRetroPalette.phosphorMoss}`,
                      color: PocketGridRetroPalette.phosphorMoss,
                      fontSize: '9px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    📂 LOAD
                  </button>

                  <button
                    onClick={() => {
                      const finalTitle = activeTitleInput.trim() || slot.name.replace(/^\d+:\s*/, '') || `Sequence ${slot.slotIndex}`;
                      onSaveToSlot(slot.slotIndex, finalTitle);
                    }}
                    title="Save currently active sequence here"
                    style={{
                      flex: 1,
                      height: '22px',
                      borderRadius: '3px',
                      background: '#282218',
                      border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                      color: PocketGridRetroPalette.tungstenAmber,
                      fontSize: '9px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    💾 SAVE HERE
                  </button>

                  <button
                    onClick={() => handleStartRename(slot.slotIndex, slot.name)}
                    title="Rename this saved slot"
                    style={{
                      width: '26px',
                      height: '22px',
                      borderRadius: '3px',
                      background: '#22242A',
                      border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                      color: PocketGridRetroPalette.warmIvoryText,
                      fontSize: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ✏️
                  </button>

                  <button
                    onClick={() => onClearSlot(slot.slotIndex)}
                    title="Clear this memory slot"
                    style={{
                      width: '26px',
                      height: '22px',
                      borderRadius: '3px',
                      background: '#241B1B',
                      border: `1px solid ${PocketGridRetroPalette.studioTapeRed}`,
                      color: PocketGridRetroPalette.studioTapeRed,
                      fontSize: '9px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pinned Modal Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 14px',
            borderTop: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            background: '#1C1D21',
            flexShrink: 0,
            minHeight: '38px',
          }}
        >
          <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText }}>
            Saved sequences are persisted locally in browser memory across reloads
          </div>

          <button
            onClick={onClose}
            style={{
              padding: '4px 14px',
              borderRadius: '4px',
              background: '#2B2D33',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              color: PocketGridRetroPalette.warmIvoryText,
              fontSize: '10px',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
